import {createUseStyles} from 'react-jss';
import loadArtemisFonts from '../../v2/components/ui/loadArtemisFonts';
import getRGB from '../../v2/style-helper/getRGB';
import MediaQueries from '../../v2/style-helper/MediaQueries';
import Spacing from '../../v2/style-helper/Spacing';
import Typography from '../../v2/style-helper/Typography';

const sideLineIcon = {
  position: 'absolute',
  height: 24,
  marginTop: 'auto',
  marginBottom: 'auto',
  top: 0,
  bottom: 0,
};

const inlineIcon = {
  height: 16,
};

const focusStyles = props => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  width: props.theme.button.large.isNarrowWidth ? '95%' : '98%',
  height: '80%',
  transform: 'translate(-50%, -50%)',
  background: 'transparent',
  boxShadow: '0 0 0 2px currentColor',
  borderRadius: (props.primary && props.theme.button.primary.borderRadius) || props.theme.button.borderRadius,
});

const getBoxShadow = (boxShadow) => {
  const rgbValue = getRGB(boxShadow.shadowColor);
  return `${boxShadow.shadowX} ${boxShadow.shadowY} ${boxShadow.shadowBlur} ${boxShadow.shadowSpread}
   rgb(${rgbValue.r}, ${rgbValue.g}, ${rgbValue.b}, ${boxShadow.shadowOpacity})`;
};

const artemisBtnAttributes = button => ({
  color: button.color,
  background: button.backgroundColor === '' ? 'transparent' : button.backgroundColor, // To override any background set prior
  backgroundColor: button.backgroundColor === '' ? 'transparent' : button.backgroundColor, // To override any color set prior
  borderColor: button.borderColor === '' ? 'transparent' : button.borderColor, // To override any color set prior
  borderRadius: button.borderRadius,
  borderWidth: button.borderWidth,
  boxShadow: getBoxShadow(button.boxShadow),
  textDecoration: button.textDecoration,
  outline: button.borderType === 'outline' && `${button.borderWidth} solid ${button.backgroundColor}`,
});

const artemisDefaultButtonStyles = button => ({
  textTransform: button.common?.font?.transform,
  ...artemisBtnAttributes(button?.regular?.default),
});

const artemisDisabledStyles = button => ({
  opacity: 1,
  ...artemisBtnAttributes(button),
});

const primaryStyles = props => ({
  textTransform: props.theme.button.primary.isUppercase && 'uppercase',
  color: props.theme.button.primary.colour,
  background: props.theme.button.primary.backgroundColour,
  borderColor: props.theme.button.primary.borderColour || props.theme.button.primary.backgroundColour,
  borderRadius: props.theme.button.primary.borderRadius,
  borderWidth: props.theme.button.primary.borderWidth,
  boxShadow: props.theme.button.primary.boxShadow,
  margin: [props.theme.button.primary.margin, 0],
  ...(props.isUsingArtemisStyles && props.theme.button.high)
  && artemisDefaultButtonStyles(props.theme.button.high),
});

const primaryHoverStyles = props => ({
  textTransform: (props.theme.button.primary.isUppercaseOnHover || props.theme.button.primary.isUppercase) && 'uppercase',
  textDecorationLine: props.theme.button.primary.hasUnderlineOnHover ? 'underline' : 'none',
  color: props.theme.button.primary.colourOnHover,
  background: props.theme.button.primary.backgroundColourOnHover,
  borderColor: props.theme.button.primary.borderColourOnHover
    || props.theme.button.primary.backgroundColourOnHover
    || props.theme.button.primary.backgroundColour,
  boxShadow: props.theme.button.primary.boxShadowOnHover,
  margin: [props.theme.button.primary.marginOnHover, 0],
  ...(props.isUsingArtemisStyles && props.theme.button.high?.regular?.hover)
  && artemisBtnAttributes(props.theme.button.high.regular.hover),
});

const roundStyles = {
  borderRadius: '50%',
  height: 24,
  width: 24,
  color: 'black',
  padding: 0,
  fontSize: 22,
  border: 'none',
  backgroundColor: '#e6e6e6',
  boxShadow: 'none',
  textShadow: 'none',
  outline: 'none',
};

const roundHoverStyles = props => ({
  color: props.theme.button.primary.colourOnHover,
  border: `1px solid ${props.theme.button.primary.borderColourOnHover}`,
  backgroundColor: props.theme.button.primary.backgroundColourOnHover,
});

const largeStyles = props => ({
  fontSize: (props.isUsingArtemisStyles && props.theme.button.medium?.common?.font?.entry
    && props.theme.typography?.[props.theme.button.medium?.common?.font?.entry])
    ? props.theme.typography[props.theme.button.medium.common.font.entry].largeDevice?.fontSize
    : props.theme.button.large.fontSize,
  lineHeight: props.theme.button.large.lineHeight,
});

const linkStyles = props => ({
  padding: 2,
  fontSize: 'inherit',
  textTransform: props.theme.core.typography.textTransform || (props.theme.button.link.isUppercase && 'uppercase') || 'initial',
  fontWeight: props.theme.button.link.fontWeight,
  fontFamily: props.theme.base.fontFamily,
  color: props.theme.button.link.colour,
  ...(props.isUsingArtemisStyles && props.theme.button.low) && artemisDefaultButtonStyles(props.theme.button.low),
  ...(props.isUsingArtemisStyles && props.theme.typography?.[Typography.body])
  && loadArtemisFonts(props.theme.typography[Typography.body], props.theme.button.isBold),
});

const linkHoverStyles = props => ({
  color: props.theme.button.link.colourOnHover || props.theme.button.primary.backgroundColour,
  ...(props.isUsingArtemisStyles && props.theme.button.low?.regular?.hover)
  && artemisBtnAttributes(props.theme.button.low.regular.hover),
});

const linkFocusStyles = props => ({
  border: `2px solid ${(props.theme.button.link.borderColourOnHover || props.theme.focusHighlightColour)}`,
  borderRadius: 0,
  padding: 2,
  ...(props.isUsingArtemisStyles && props.theme.button.low?.regular?.focus)
  && artemisBtnAttributes(props.theme.button.low.regular.focus),
});

const textOnlyStyles = {
  textDecoration: 'underline',
  border: '2px solid transparent',
  background: 'none',
  backgroundColour: 'none',
  boxShadow: 'none',
  outline: 'none',
};

const noStyles = {
  textDecoration: 'none',
  outline: 'none',
};

const textOnlyHoverStyles = props => ({
  // duplicated default styles to override Button hover styles
  ...textOnlyStyles,
  ...noStyles,
  color: props.theme.button.textOnly?.colourOnHover || 'inherit',
});

const genericHoverStyles = props => ({
  textDecorationLine: props.theme.button.secondary.hasUnderlineOnHover && 'underline',
  textDecorationThickness: props.theme.button.underlineThickness,
  boxShadow: props.theme.button.boxShadowOnHover,
  outline: props.theme.button.outlineHover,
  color: props.theme.button.secondary.colourOnHover,
  backgroundColor: props.theme.button.secondary.backgroundColourOnHover,
  borderColor: props.theme.button.secondary.borderColourOnHover || props.theme.button.secondary.backgroundColourOnHover,
  borderWidth: props.theme.button.borderWidthOnHover,
  margin: [props.theme.button.marginOnHover, 0],
  
  ...(props.isUsingArtemisStyles && props.theme.button.medium?.regular?.hover)
  && artemisBtnAttributes(props.theme.button.medium.regular.hover),
  ...props.primary && primaryHoverStyles(props),
  ...props.link && linkHoverStyles(props),
  ...(props.textOnly || props.link) && textOnlyHoverStyles(props),
  ...props.round && roundHoverStyles(props),
});

const artemisActiveStyles = props => ({
  ...props.theme.button.medium?.regular?.active
  && artemisBtnAttributes(props.theme.button.medium.regular.active),
  ...(props.primary && props.theme.button.high?.regular?.active)
  && artemisBtnAttributes(props.theme.button.high.regular.active),
  ...(props.link && props.theme.button.low?.regular?.active)
  && artemisBtnAttributes(props.theme.button.low.regular.active),
});

export default createUseStyles({
  button: {
    '& + .Button': {
      [MediaQueries.phone]: {
        marginTop: '1em',
      },
      '&.Button--horizontal-align': {
        marginTop: 0,
      },
    },
    '& .Translation': {
      transform: props => `translateY(${props.theme.button.translateY}px)`,
    },
    
    transition: '0.2s linear all',
    cursor: 'pointer',
    display: props => (props.link ? 'inline' : 'flex'),
    justifyContent: props => (props.leftAlign ? 'flex-start' : 'center'),
    alignItems: 'center',
    position: 'relative',
    width: (props) => {
      if (props.fullWidth) return '100%';
      if (props.maxContent) return 'max-content';
    },
    margin: props => [props.theme.button.margin, 0],
    padding: props => `${props.theme.button.verticalPadding} 1em`,
    textAlign: props => (props.leftAlign ? 'left' : 'center'),
    boxShadow: props => props.theme.button.boxShadow,
    outline: props => props.theme.button.outline,
    outlineOffset: props => props.theme.button.outlineOffset,
    textUnderlineOffset: props => props.theme.button.underlineOffset,
    
    fontSize: props => props.theme.button.fontSize,
    fontFamily: props => props.theme.button.fontFamily,
    fontWeight: props => props.theme.button.fontWeight,
    fontStretch: props => props.theme.button.fontStretch,
    lineHeight: props => props.theme.button.lineHeight,
    letterSpacing: props => props.theme.button.letterSpacing,
    textTransform: props => props.theme.button.isUppercase && 'uppercase',
    
    borderStyle: 'solid',
    borderWidth: props => props.theme.button.borderWidth,
    borderRadius: props => props.theme.button.borderRadius,
    borderColor: props => props.theme.button.secondary.borderColour || props.theme.button.secondary.backgroundColour,
    color: props => props.theme.button.secondary.colour,
    backgroundColor: props => props.theme.button.secondary.backgroundColour,
    
    extend: props => ({
      ...(props.isUsingArtemisStyles && props.theme.button.medium?.common?.font?.entry
        && props.theme.typography?.[props.theme.button.medium?.common?.font?.entry])
      && loadArtemisFonts(props.theme.typography[props.theme.button.medium.common.font.entry], props.theme.button.isBold),
      ...(props.isUsingArtemisStyles && props.theme.button.medium)
      && artemisDefaultButtonStyles(props.theme.button.medium),
      ...props.large && largeStyles(props),
      ...props.primary && primaryStyles(props),
      ...props.link && linkStyles(props),
      ...(props.textOnly || props.link) && textOnlyStyles,
      ...props.noStyle && noStyles, // To override underline from textOnlyStyles
      ...props.round && roundStyles,
    }),
    
    '&:disabled, &[disabled]': {
      cursor: 'not-allowed',
      opacity: 0.5,
      
      extend: props => ({
        ...genericHoverStyles(props),
        ...(props.isUsingArtemisStyles && props.theme.button.medium?.regular?.disabled)
        && artemisDisabledStyles(props.theme.button.medium.regular.disabled),
        ...(props.primary && props.isUsingArtemisStyles && props.theme.button.high?.regular?.disabled)
        && artemisBtnAttributes(props.theme.button.high.regular.disabled),
        ...(props.link && props.isUsingArtemisStyles && props.theme.button.low?.regular?.disabled)
        && artemisBtnAttributes(props.theme.button.low.regular.disabled),
      }),
    },
  
    '&:hover, &:active': {
      '&:not([disabled])': {
        extend: props => ({
          ...genericHoverStyles(props),
        }),
      },
      extend: props => ({
        ...!props.isUsingArtemisStyles && genericHoverStyles(props),
      }),
    },
  
    '&:active': {
      extend: props => ({
        ...(props.isUsingArtemisStyles && props.theme.button) && artemisActiveStyles(props),
      }),
    },
    
    '&:focus': {
      extend: props => ({
        ...genericHoverStyles(props),
        ...(props.isUsingArtemisStyles && props.theme.button.medium?.regular?.focus)
        && artemisBtnAttributes(props.theme.button.medium.regular.focus),
        ...(props.primary && props.isUsingArtemisStyles && props.theme.button.high?.regular?.focus)
        && artemisBtnAttributes(props.theme.button.high.regular.focus),
        ...(props.textOnly || props.link) && textOnlyHoverStyles(props),
        ...props.link && linkFocusStyles(props),
      }),
      
      '& .Button--focus': {
        extend: props => ({
          ...focusStyles(props),
          display: (props.textOnly || props.link || props.round) && 'none',
        }),
      },
    },
    
    [MediaQueries.phone]: {
      width: props => ((props.noResize && 'auto') || (props.round && Spacing.triple) || '100%'),
      lineHeight: props => props.large && props.theme.button.large.mobileLineHeight,
    },
    
    '& svg': {
      fill: 'currentColor',
      display: 'block',
    },
  },
  
  icon: {
    margin: [0, Spacing.single],
  },
  iconLeft: {
    extend: props => (props.theme.button.hasInlineIcons ? inlineIcon : sideLineIcon),
    marginRight: props => props.theme.button.hasInlineIcons && Spacing.single,
    left: props => !props.theme.button.hasInlineIcons && Spacing.double,
  },
  iconRight: {
    extend: props => (props.theme.button.hasInlineIcons || props.inLineIcon ? inlineIcon : sideLineIcon),
    marginLeft: props => props.theme.button.hasInlineIcons && Spacing.single,
    right: props => !props.theme.button.hasInlineIcons && Spacing.double,
    '&:last-child': {
      marginBottom: 'auto', // To overwrite the :last-child margin: 0 styling
    },
  },
});
